/**
 * Training Mode — Spotlight overlay + speech bubble.
 *
 * Spec §3A:
 *   - Target element: blue ring (box-shadow 0 0 0 4px #2563eb), elevated z-index,
 *     remains fully interactive.
 *   - Dim overlay: rgba(0,0,0,0.15) fixed fullscreen, BELOW the target but
 *     ABOVE other page content.
 *   - Speech bubble: 200px max, arrow pointer toward target, auto-positioned
 *     to avoid going off-screen.
 *
 * Robustness:
 *   - Re-renders on scroll/resize/mutation so the spotlight follows the target.
 *   - Target missing (selector returned null) → shows a small "Can't find this
 *     element on the current page" fallback bubble near the top-right, with a
 *     Skip button.
 *   - Multiple matches → uses the first visible one (boundingClientRect.width > 0).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";

const TARGET_ATTR = "data-training-spotlight";
const TARGET_CLASS = "training-spotlight-target";

function injectStyles() {
  if (document.getElementById("training-spotlight-styles")) return;
  const el = document.createElement("style");
  el.id = "training-spotlight-styles";
  el.textContent = `
    .${TARGET_CLASS} {
      position: relative !important;
      z-index: 2147483646 !important;
      box-shadow: 0 0 0 4px #2563eb, 0 0 0 8px rgba(37,99,235,0.15) !important;
      border-radius: 6px !important;
      transition: box-shadow 0.18s ease !important;
    }
    @keyframes trainingSpotlightPulse {
      0%, 100% { box-shadow: 0 0 0 4px #2563eb, 0 0 0 8px rgba(37,99,235,0.15); }
      50%      { box-shadow: 0 0 0 4px #2563eb, 0 0 0 14px rgba(37,99,235,0.10); }
    }
    .${TARGET_CLASS} { animation: trainingSpotlightPulse 1.6s ease-in-out infinite; }
  `;
  document.head.appendChild(el);
}

function pickVisible(list) {
  for (const el of list) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return list[0] || null;
}

function computeBubblePos(rect, vw, vh, bubbleW = 260, bubbleH = 140) {
  const margin = 12;
  // prefer below
  if (rect.bottom + bubbleH + margin < vh) {
    const left = Math.max(margin, Math.min(vw - bubbleW - margin, rect.left + rect.width / 2 - bubbleW / 2));
    return { top: rect.bottom + margin, left, placement: "below" };
  }
  // above
  if (rect.top - bubbleH - margin > 0) {
    const left = Math.max(margin, Math.min(vw - bubbleW - margin, rect.left + rect.width / 2 - bubbleW / 2));
    return { top: rect.top - bubbleH - margin, left, placement: "above" };
  }
  // right
  if (rect.right + bubbleW + margin < vw) {
    const top = Math.max(margin, Math.min(vh - bubbleH - margin, rect.top + rect.height / 2 - bubbleH / 2));
    return { top, left: rect.right + margin, placement: "right" };
  }
  // left fallback
  const top = Math.max(margin, Math.min(vh - bubbleH - margin, rect.top + rect.height / 2 - bubbleH / 2));
  return { top, left: Math.max(margin, rect.left - bubbleW - margin), placement: "left" };
}

export default function Spotlight({
  selector,
  step,
  total,
  instruction,
  onNext,
  onBack,
  onSkip,
  onFinish,
  onClickTarget,
  canBack = true,
  isLastStep = false,
  pauseAtAction = false,
  onPausedAction,
}) {
  const [rect, setRect] = useState(null);
  const [missing, setMissing] = useState(false);
  const targetRef = useRef(null);

  useEffect(() => { injectStyles(); }, []);

  useLayoutEffect(() => {
    if (!selector) {
      setMissing(true);
      return undefined;
    }
    let prev = targetRef.current;
    if (prev) {
      prev.classList.remove(TARGET_CLASS);
      prev.removeAttribute(TARGET_ATTR);
    }
    let el = null;
    try {
      const list = document.querySelectorAll(selector);
      el = pickVisible(Array.from(list));
    } catch {
      el = null;
    }
    // If the target isn't present yet (route just changed, lazy chunk
    // hydrating, tab not activated), poll for up to 4s (8 × 500ms) before
    // giving up. Every tick re-queries so the observer picks up late-mounted
    // targets without forcing the user to Skip.
    let retryTimer = null;
    let retriesLeft = 8;
    const tryFind = () => {
      try {
        const list = document.querySelectorAll(selector);
        return pickVisible(Array.from(list));
      } catch { return null; }
    };
    if (!el) {
      setMissing(true);  // show fallback card immediately so users aren't staring at a black overlay
      setRect(null);
      targetRef.current = null;
      const poll = () => {
        if (retriesLeft-- <= 0) return;
        const found = tryFind();
        if (!found) {
          retryTimer = setTimeout(poll, 500);
          return;
        }
        el = found;
        setMissing(false);
        el.classList.add(TARGET_CLASS);
        el.setAttribute(TARGET_ATTR, "true");
        targetRef.current = el;
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
      };
      retryTimer = setTimeout(poll, 500);
      return () => { if (retryTimer) clearTimeout(retryTimer); };
    }
    setMissing(false);
    el.classList.add(TARGET_CLASS);
    el.setAttribute(TARGET_ATTR, "true");
    targetRef.current = el;

    const measure = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height, right: r.right, bottom: r.bottom });
    };
    measure();
    // Scroll target into view if off-screen
    const vh = window.innerHeight;
    const r = el.getBoundingClientRect();
    if (r.top < 60 || r.bottom > vh - 60) {
      try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* noop */ }
      setTimeout(measure, 320);
    }

    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    const mo = new MutationObserver(measure);
    mo.observe(document.body, { attributes: true, childList: true, subtree: true, characterData: true });
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    // Auto-advance on click of the spotlit element
    const onClick = (e) => {
      if (typeof onClickTarget === "function") onClickTarget(e);
      if (pauseAtAction) {
        if (typeof onPausedAction === "function") onPausedAction(e);
      } else {
        // auto-advance — wait a short tick so the user sees their click land
        setTimeout(() => { onNext?.(); }, 180);
      }
    };
    el.addEventListener("click", onClick, { capture: true });

    return () => {
      el.classList.remove(TARGET_CLASS);
      el.removeAttribute(TARGET_ATTR);
      el.removeEventListener("click", onClick, { capture: true });
      if (ro) ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately re-runs on selector change; onNext/onClickTarget/pauseAtAction are stable within a step lifetime
  }, [selector]);

  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 720;

  let bubble = null;
  if (rect && !missing) {
    bubble = computeBubblePos(rect, vw, vh);
  }

  return (
    <>
      {/* dim overlay */}
      <div
        data-testid="training-spotlight-overlay"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.15)",
          zIndex: 2147483640,
          pointerEvents: "none",
        }}
      />
      {missing && (
        <div
          data-testid="training-spotlight-missing"
          style={{
            position: "fixed",
            top: 80,
            right: 24,
            width: 280,
            background: "#fff",
            border: "0.5px solid #ddd",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: 12,
            fontSize: 12,
            color: "#111",
            zIndex: 2147483647,
          }}
        >
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            Looking for this element…
          </div>
          <div style={{ color: "#888", fontSize: 11, marginBottom: 8 }}>
            {instruction}
          </div>
          <div style={{ color: "#888", fontSize: 10, marginBottom: 8 }}>
            If the page just loaded, it may take a moment. You can jump ahead if it's not available in your role.
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "space-between" }}>
            {canBack && <button onClick={onBack} data-testid="training-bubble-back" style={{ border: "0.5px solid #ddd", background: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>← Back</button>}
            <button onClick={onSkip} data-testid="training-bubble-skip" style={{ border: "0.5px solid #ddd", background: "#fff", color: "#666", fontSize: 11, cursor: "pointer", borderRadius: 6, padding: "4px 10px" }}>Next step →</button>
            <button onClick={isLastStep ? onFinish : onNext} data-testid="training-bubble-next" style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer" }}>
              {isLastStep ? "Finish ✓" : "Continue"}
            </button>
          </div>
        </div>
      )}
      {bubble && !missing && (
        <div
          data-testid="training-spotlight-bubble"
          style={{
            position: "fixed",
            top: bubble.top,
            left: bubble.left,
            width: 260,
            background: "#fff",
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
            padding: 12,
            zIndex: 2147483647,
            fontSize: 12,
            color: "#111",
          }}
        >
          <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
            Step {step} of {total}
          </div>
          <div style={{ fontWeight: 500, lineHeight: 1.5, marginBottom: 10 }}>
            {instruction}
          </div>
          {pauseAtAction && (
            <div style={{ fontSize: 10, color: "#185FA5", marginBottom: 6 }}>
              Pause-at-my-action is ON — click the highlighted element when ready.
            </div>
          )}
          <div style={{ display: "flex", gap: 6, justifyContent: "space-between", alignItems: "center" }}>
            {canBack ? (
              <button onClick={onBack} data-testid="training-bubble-back" style={{ border: "0.5px solid #ddd", background: "#fff", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer" }}>← Back</button>
            ) : <span />}
            <button onClick={onSkip} data-testid="training-bubble-skip" style={{ border: "none", background: "transparent", color: "#888", fontSize: 11, cursor: "pointer" }}>Skip</button>
            <button onClick={isLastStep ? onFinish : onNext} data-testid="training-bubble-next" style={{ background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", fontSize: 11, fontWeight: 500, cursor: "pointer" }}>
              {isLastStep ? "Finish ✓" : "Next →"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
